import { forwardRef, type ReactNode, type HTMLAttributes } from 'react';
import { cn } from '@vitness/theme';

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Primary page title, rendered as an `<h1>` in the display font. */
  title: ReactNode;
  /** Optional small uppercase label rendered above the title. */
  eyebrow?: ReactNode;
  /** Optional supporting subtitle rendered below the title. */
  subtitle?: ReactNode;
  /** Optional right-aligned action slot (e.g. buttons), kept on the same row. */
  actions?: ReactNode;
}

/**
 * Page-level header with an optional eyebrow, a title, an optional subtitle,
 * and a right-aligned actions slot. The title/actions sit on one row that
 * wraps gracefully on narrow viewports. Token-driven typography and colors.
 */
export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  { title, eyebrow, subtitle, actions, className, ...props },
  ref,
) {
  return (
    <header
      ref={ref}
      className={cn(
        'flex flex-wrap items-start justify-between gap-vn-4 pb-vn-5',
        className,
      )}
      {...props}
    >
      <div className="min-w-0">
        {eyebrow && (
          <div className="mb-vn-1 font-mono text-xs uppercase tracking-wide text-text-muted">
            {eyebrow}
          </div>
        )}
        <h1 className="font-display text-text-primary font-bold text-xl leading-tight tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-vn-1 font-body text-text-secondary text-sm">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-vn-2 shrink-0">{actions}</div>}
    </header>
  );
});
